import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../../stores/gameStore';
import { playSound } from '../../utils/soundService';

interface BadgeProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Badge({ isOpen, onClose }: BadgeProps) {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [imageRotate, setImageRotate] = useState(0);
  const badgeRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const { ndaSigned, badgeActivated, setBadgeActivated } = useGameStore(
    (state) => state,
  );

  const [cornerStates, setCornerStates] = useState({
    topLeft: null as string | null,
    topRight: null as string | null,
    bottomRight: null as string | null,
    bottomLeft: null as string | null,
  });
  const isPattern =
    cornerStates.topLeft === 'L' &&
    cornerStates.topRight === '♠' &&
    cornerStates.bottomLeft === '♥' &&
    cornerStates.bottomRight === 'X';

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!badgeRef.current) return;

    const rect = badgeRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const x = e.clientX - centerX;
    const y = e.clientY - centerY;

    const rotateX = (y / (rect.height / 2)) * -8;
    const rotateY = (x / (rect.width / 2)) * 8;

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
  };

  const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const rotationAmount = (x / rect.width) * 20 - 10; // Range from -10 to 10

    setImageRotate(rotationAmount);
  };

  const handleImageMouseLeave = () => {
    setImageRotate(0);
  };

  const handleCornerClick = (
    corner: keyof typeof cornerStates,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    playSound('click');

    setCornerStates((prev) => {
      const current = prev[corner];
      const states = [null, '♦', 'L', 'X', '♥', '♣', '♠'];
      const currentIndex = states.indexOf(current);
      const next = states[(currentIndex + 1) % states.length];

      return {
        ...prev,
        [corner]: next,
      };
    });
  };

  useEffect(() => {
    if (badgeActivated) {
      return;
    }

    if (isPattern) {
      playSound('badge-unlock');
      setBadgeActivated(true);
    }
  }, [badgeActivated, isPattern, setBadgeActivated]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center shadow-lg"
      style={{ perspective: '1000px', zIndex: 9999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setCornerStates({
            topLeft: null,
            topRight: null,
            bottomRight: null,
            bottomLeft: null,
          });
          onClose();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setCornerStates({
            topLeft: null,
            topRight: null,
            bottomRight: null,
            bottomLeft: null,
          });
          onClose();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div
        ref={badgeRef}
        className="relative bg-white/70 rounded-lg shadow-lg w-70 transition-transform duration-100"
        style={{
          transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
          transformStyle: 'preserve-3d',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Close button */}
        <div className="flex p-3 px-10">laura.ai</div>

        {/* Image in the middle */}
        <div
          ref={imageRef}
          className="flex items-center justify-center min-h-64 border m-2"
          onMouseMove={handleImageMouseMove}
          onMouseLeave={handleImageMouseLeave}
          style={{
            backgroundColor: '#e5e5f7',
            opacity: badgeActivated ? 0.8 : 0.1,
            backgroundImage: `
              linear-gradient(135deg, #3a3a3a 25%, transparent 25%),
              linear-gradient(225deg, #3a3a3a 25%, transparent 25%),
              linear-gradient(45deg, #3a3a3a 25%, transparent 25%),
              linear-gradient(315deg, #3a3a3a 25%, #e5e5f7 25%)
            `,
            backgroundPosition: `${imageRotate}px 0, ${imageRotate}px 0, 0 0, 0 0`,
            backgroundSize: '20px 20px',
            backgroundRepeat: 'repeat',
          }}
        />

        {/* Name at bottom */}
        <div
          className="m-4 text-center"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <motion.p
            className={`text-lg font-semibold text-gray-900 h-6 select-text ${isHovered ? 'border border-red-500' : ''}`}
          >
            {isHovered || badgeActivated ? (
              <motion.span>
                {'Trent Tres'.split('').map((char, pos) => (
                  <motion.span
                    key={`char-${pos}`}
                    initial={{ opacity: badgeActivated && !isPattern ? 1 : 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 2,
                      delay: pos * 0.3,
                    }}
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.span>
            ) : (
              ''
            )}
          </motion.p>
        </div>
        <p className="text-xs text-center font-semibold text-gray-900 my-4">
          trent.1766
        </p>

        {/* Invisible corner buttons */}
        {!badgeActivated && ndaSigned && (
          <>
            <button
              type="button"
              onClick={(e) => handleCornerClick('topLeft', e)}
              className="absolute top-0 left-0 w-8 h-8 cursor-pointer text-black hover:bg-black hover:text-white hover:opacity-50 transition-all"
              style={{ borderRadius: '8px 0 8px 0' }}
            >
              {cornerStates.topLeft && (
                <span className="text-lg font-semibold">
                  {cornerStates.topLeft}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={(e) => handleCornerClick('topRight', e)}
              className="absolute top-0 right-0 w-8 h-8 cursor-pointer text-black hover:bg-black hover:text-white hover:opacity-50 transition-all"
              style={{ borderRadius: '0 8px 0 8px' }}
            >
              {cornerStates.topRight && (
                <span className="text-lg font-semibold">
                  {cornerStates.topRight}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={(e) => handleCornerClick('bottomRight', e)}
              className="absolute bottom-0 right-0 w-8 h-8 cursor-pointer text-black hover:bg-black hover:text-white hover:opacity-50 transition-all"
              style={{ borderRadius: '8px 0 8px 0' }}
            >
              {cornerStates.bottomRight && (
                <span className="text-lg font-semibold">
                  {cornerStates.bottomRight}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={(e) => handleCornerClick('bottomLeft', e)}
              className="absolute bottom-0 left-0 w-8 h-8 cursor-pointer text-black hover:bg-black hover:text-white hover:opacity-50 transition-all"
              style={{ borderRadius: '0 8px 0 8px' }}
            >
              {cornerStates.bottomLeft && (
                <span className="text-lg font-semibold">
                  {cornerStates.bottomLeft}
                </span>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
